import React, { useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronLeft, ChevronRight, PackageSearch, RefreshCw } from "lucide-react";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
    fetchProductsByKeyword,
    fetchProductsBySubCategory,
    selectSimilarProducts,
    selectSimilarProductsStatus,
    selectProductError,
} from "store/slices/productsSlice";
import ProductCard from "@/components/products/ProductCard";
import { addToCart } from "store/slices/cartSlice";
import { IKeyword } from "@/Interfaces/interface.productsapi";

interface ArrowProps {
    onClick?: () => void;
}

const PrevArrow: React.FC<ArrowProps> = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-all border border-gray-100"
            aria-label="Previous"
        >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
    );
};

const NextArrow: React.FC<ArrowProps> = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-all border border-gray-100"
            aria-label="Next"
        >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>
    );
};

interface SimilarProductsProps {
    subCategoryId?: string;
    currentProductId?: string;
}

const SimilarProducts: React.FC<SimilarProductsProps> = ({ subCategoryId, currentProductId }) => {
    const dispatch = useAppDispatch();
    const similarProducts = useAppSelector(selectSimilarProducts);
    const status = useAppSelector(selectSimilarProductsStatus);
    const error = useAppSelector(selectProductError);
    useEffect(() => {
        if (subCategoryId) {
            dispatch(fetchProductsBySubCategory([subCategoryId]));
        }
    }, [dispatch, subCategoryId]);

    if (!subCategoryId) {
        return null;
    }

    const handleRetry = () => {
        dispatch(fetchProductsBySubCategory([subCategoryId]));
    };

    const ProductSkeleton = () => (
        <div className="min-w-[280px] w-[280px] border border-gray-200 rounded-lg p-4 animate-pulse bg-white">
            <div className="h-48 bg-gray-200 rounded-md mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
        </div>
    );


    const displayedProducts = currentProductId
        ? similarProducts.filter(p => p.id !== currentProductId)
        : similarProducts;


    const count = displayedProducts.length;

    const settings = {
        dots: false,
        infinite: count > 4,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        centerMode: false,
        arrows: count > 4,
        prevArrow: count > 4 ? <PrevArrow /> : <></>,
        nextArrow: count > 4 ? <NextArrow /> : <></>,
        responsive: [
            {
                breakpoint: 1280,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                    centerMode: false,
                    arrows: count > 3,
                    infinite: count > 3,
                },
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                    centerMode: false,
                    arrows: count > 3,
                    infinite: count > 3,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    centerMode: false,
                    arrows: count > 2,
                    infinite: count > 2,
                },
            },
            {
                breakpoint: 640,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    arrows: count > 2,
                    infinite: count > 2,
                },
            },
        ],
    };

    return (
        <div className="mt-16 w-full relative group">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore Similar products</h2>

            {status === "loading" && (
                <div className="flex gap-6 overflow-hidden">
                    {[1, 2, 3, 4].map((i) => (
                        <ProductSkeleton key={i} />
                    ))}
                </div>
            )}

            {status === "failed" && (
                <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
                    <p className="text-red-500 mb-4">{error || "Failed to load similar products"}</p>
                    <button
                        onClick={handleRetry}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw size={18} />
                        Try Again
                    </button>
                </div>
            )}

            {status === "succeeded" && displayedProducts.length === 0 && (
                <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg text-gray-500">
                    <PackageSearch size={48} className="mb-2 opacity-50" />
                    <p>No similar products found.</p>
                </div>
            )}

            {status === "succeeded" && displayedProducts.length > 0 && (
                <div className="px-2 [&_.slick-track]:!ml-0 [&_.slick-list]:!text-left">
                    <Slider {...settings} className="-mx-2">
                        {displayedProducts.map((product) => (
                            <div key={product.id} className="px-3 py-2">
                                <ProductCard
                                    data={product}
                                    dispatch={dispatch}
                                    addToCart={addToCart}
                                />
                            </div>
                        ))}
                    </Slider>
                </div>
            )}
        </div>
    );
};

export default SimilarProducts;